<template>
  <div class="flow-template-preview" :style="{ height: `${height}px` }">
    <div class="preview-grid"></div>
    <div v-if="previewScene.nodes.length" class="flowchart-preview-stage">
      <simple-flowchart
        ref="flowchart"
        class="flowchart-preview-render"
        :style="previewRenderStyle"
        :scene="previewScene"
        :height="previewContentHeight"
        @hook:mounted="aplicarCores"
      />
    </div>
    <div v-else class="preview-empty">
      <span>0</span>
      <p>Fluxo sem estágios</p>
    </div>
  </div>
</template>

<script>
import SimpleFlowchart from "vue-simple-flowchart";
import "vue-simple-flowchart/dist/vue-flowchart.css";

export default {
  components: {
    SimpleFlowchart
  },
  props: {
    fluxo: {
      type: Object,
      default: () => ({ nodes: [], links: [] })
    },
    height: {
      type: Number,
      default: 390
    },
    nodeStatuses: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      canvasWidth: 720,
      resizeObserver: null
    };
  },
  computed: {
    previewLayout() {
      const nodes = this.fluxo.nodes || [];
      if (!nodes.length) {
        return {
          scene: { centerX: 0, centerY: 0, scale: 1, nodes: [], links: [] },
          contentWidth: this.canvasWidth,
          contentHeight: this.height,
          scale: 1,
          offsetX: 0,
          offsetY: 0
        };
      }

      const nodeWidth = 100;
      const nodeHeight = 80;
      const padding = 35;
      const nodesComPosicao = nodes.map((node, index) => ({
        ...node,
        x: Number.isFinite(Number(node.x)) ? Number(node.x) : (index % 4) * 180,
        y: Number.isFinite(Number(node.y))
          ? Number(node.y)
          : Math.floor(index / 4) * 90
      }));
      const xs = nodesComPosicao.map((node) => node.x);
      const ys = nodesComPosicao.map((node) => node.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);
      const contentWidth = maxX - minX + nodeWidth + padding * 2;
      const contentHeight = maxY - minY + nodeHeight + padding * 2;
      const scale = Math.min(
        (this.canvasWidth - 20) / contentWidth,
        (this.height - 20) / contentHeight,
        1
      );
      const ids = new Set(nodesComPosicao.map((node) => node.id));
      const links = (this.fluxo.links || []).filter(
        (link) => ids.has(link.from) && ids.has(link.to)
      );

      return {
        scene: {
          centerX: padding - minX,
          centerY: padding - minY,
          scale: 1,
          nodes: nodesComPosicao,
          links
        },
        contentWidth,
        contentHeight,
        scale,
        offsetX: (this.canvasWidth - contentWidth * scale) / 2,
        offsetY: (this.height - contentHeight * scale) / 2
      };
    },
    previewScene() {
      return this.previewLayout.scene;
    },
    previewContentHeight() {
      return Math.ceil(this.previewLayout.contentHeight);
    },
    previewRenderStyle() {
      const layout = this.previewLayout;

      return {
        width: `${layout.contentWidth}px`,
        height: `${layout.contentHeight}px`,
        transform: `translate(${layout.offsetX}px, ${layout.offsetY}px) scale(${layout.scale})`
      };
    }
  },
  watch: {
    fluxo: {
      handler() {
        this.$nextTick(this.aplicarCores);
      },
      deep: true
    },
    nodeStatuses: {
      handler() {
        this.$nextTick(this.aplicarCores);
      },
      deep: true
    },
    canvasWidth() {
      this.$nextTick(this.aplicarCores);
    }
  },
  mounted() {
    this.atualizarLargura();
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(this.atualizarLargura);
      this.resizeObserver.observe(this.$el);
    } else {
      window.addEventListener("resize", this.atualizarLargura);
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    window.removeEventListener("resize", this.atualizarLargura);
  },
  methods: {
    atualizarLargura() {
      if (this.$el && this.$el.clientWidth) {
        this.canvasWidth = this.$el.clientWidth;
      }
    },
    aplicarCores() {
      const componente = this.$refs.flowchart;
      if (!componente || !componente.$el) return;

      const elementos = componente.$el.querySelectorAll(".flowchart-node");
      elementos.forEach((elemento, index) => {
        const node = this.previewScene.nodes[index];
        if (!node) return;

        elemento.classList.remove(
          "preview-node-added",
          "preview-node-removed",
          "preview-node-changed"
        );
        const status = this.nodeStatuses[String(node.id)];
        if (status) elemento.classList.add(`preview-node-${status}`);

        const cor = node.color || node.cor || (node.ces_metadata || {}).cor;
        if (typeof cor !== "string" || !/^#[0-9a-f]{3,8}$/i.test(cor)) return;

        elemento.style.background = cor;
        const label = elemento.querySelector(".node-label");
        if (label) label.style.color = this.getContrastColor(cor);
      });
    },
    getContrastColor(hex) {
      const cor = hex.replace("#", "");
      const valor = cor.length === 3 ? cor.replace(/(.)/g, "$1$1") : cor;
      const r = parseInt(valor.substring(0, 2), 16);
      const g = parseInt(valor.substring(2, 4), 16);
      const b = parseInt(valor.substring(4, 6), 16);
      const luminancia = 0.299 * r + 0.587 * g + 0.114 * b;

      return luminancia > 186 ? "#263247" : "#ffffff";
    }
  }
};
</script>

<style lang="scss" scoped>
.flow-template-preview {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: #fafbfd;
}

.preview-grid {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(#d8dee9 1px, transparent 1px);
  background-size: 20px 20px;
}

.flowchart-preview-stage {
  position: absolute;
  inset: 0;
}

.flowchart-preview-render {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  transform-origin: top left;
}

::v-deep .flowchart-preview-render.flowchart-container {
  overflow: visible;
  background: transparent;
}

::v-deep .flowchart-preview-render .flowchart-node {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100px;
  height: fit-content;
  min-height: 75px;
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(42, 53, 72, 0.16);

  &.preview-node-added {
    box-shadow: 0 0 0 4px rgba(39, 132, 82, 0.45),
      0 5px 12px rgba(42, 53, 72, 0.18);
  }

  &.preview-node-removed {
    box-shadow: 0 0 0 4px rgba(197, 75, 75, 0.45),
      0 5px 12px rgba(42, 53, 72, 0.18);
  }

  &.preview-node-changed {
    box-shadow: 0 0 0 4px rgba(57, 117, 202, 0.45),
      0 5px 12px rgba(42, 53, 72, 0.18);
  }
}

::v-deep .flowchart-preview-render .node-main {
  margin: auto;
}

::v-deep .flowchart-preview-render .node-type,
::v-deep .flowchart-preview-render .node-port,
::v-deep .flowchart-preview-render .node-delete {
  display: none !important;
}

::v-deep .flowchart-preview-render .node-label {
  display: -webkit-box;
  overflow: hidden;
  font-size: 12px !important;
  font-weight: 600;
  line-height: 1.25;
  text-overflow: ellipsis;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

::v-deep .flowchart-preview-render svg g {
  transform: translateX(10px) !important;
}

.preview-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: #8993a3;

  span {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 68px;
    height: 68px;
    border-radius: 50%;
    background: #edf1f6;
    font-size: 18px;
    font-weight: 700;
  }

  p {
    margin: 14px 0 0;
    font-size: 12px;
  }
}
</style>
